import { CaratzonLogo } from "~/components/marketing/svg/CaratzonLogo";
import { useT } from "~/i18n/context";

// ── Design tokens baked in (footer sits on India-ink dark, not the cream theme)
const INK = "oklch(0.085 0.004 50)";
const RULE = "oklch(0.24 0.005 55)";
const TEXT_PRIMARY = "oklch(0.95 0.006 88)";
const TEXT_DIM = "oklch(0.72 0.010 78)";
const TEXT_GHOST = "oklch(0.52 0.008 68)";
const TEAL = "oklch(0.55 0.10 213)";
const TEAL_HOVER = "oklch(0.62 0.10 213)";
const GOLD = "oklch(0.62 0.072 52)";

function ExternalIcon() {
  return (
    <svg
      viewBox="0 0 10 10"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      className="inline-block ml-0.5 mb-0.5"
      style={{ width: "0.55em", height: "0.55em" }}
      aria-hidden="true"
    >
      <path d="M2 8 8 2M4 2h4v4" />
    </svg>
  );
}

function FacetMark() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" style={{ width: 14, height: 14 }}>
      <polygon
        points="10,2 17,7 17,13 10,18 3,13 3,7"
        fill="none"
        stroke={GOLD}
        strokeWidth="0.8"
        opacity={0.45}
      />
      <line x1="10" y1="2" x2="10" y2="18" stroke={GOLD} strokeWidth="0.5" opacity={0.2} />
      <line x1="3" y1="10" x2="17" y2="10" stroke={GOLD} strokeWidth="0.5" opacity={0.2} />
    </svg>
  );
}

export function MarketingFooter({ audience = "neutral" }: { audience?: "brand" | "designer" | "neutral" }) {
  const { t } = useT();
  const f = t.footer.links as Record<string, string>;
  const year = new Date().getFullYear();

  const brandPlatformLinks = [
    { label: f.forBrands, href: "/brands" },
    { label: f.howItWorks ?? "How It Works", href: "/brands/how-it-works" },
    { label: f.caraAi ?? "Cara AI", href: "/brands/ai" },
    { label: f.aiCredits ?? "AI Credits", href: "/ai/credits" },
    { label: f.tradeCatalogue, href: "/sign-in?redirect=/trade/catalog" },
    { label: f.accessPlatform, href: "/sign-in" },
    { label: f.becomeManufacturer ?? "Become a Manufacturing Partner", href: "/for-manufacturers" },
  ];

  const designerPlatformLinks = [
    { label: f.forDesigners, href: "/designers" },
    { label: f.inspire, href: "/inspire" },
    { label: f.cadStandards, href: "/designers#cad-standards" },
    { label: f.accessPlatform, href: "/sign-in" },
    { label: f.becomeManufacturer ?? "Become a Manufacturing Partner", href: "/for-manufacturers" },
  ];

  const neutralPlatformLinks = [
    { label: f.forDesigners, href: "/designers" },
    { label: f.forBrands, href: "/brands" },
    { label: f.caraAi ?? "Cara AI", href: "/brands/ai" },
    { label: f.tradeCatalogue, href: "/sign-in?redirect=/trade/catalog" },
    { label: f.accessPlatform, href: "/sign-in" },
    { label: f.becomeManufacturer ?? "Become a Manufacturing Partner", href: "/for-manufacturers" },
  ];

  const platformLinks =
    audience === "brand" ? brandPlatformLinks :
    audience === "designer" ? designerPlatformLinks :
    neutralPlatformLinks;

  const COLUMNS = [
    {
      heading: t.footer.columns.platform,
      links: platformLinks,
    },
    {
      heading: t.footer.columns.company,
      links: [
        { label: t.footer.links.aboutCaratzon, href: "/about" },
        { label: t.footer.links.ourStory, href: "/about#story" },
        { label: t.footer.links.leadership, href: "/about#leadership" },
        { label: t.footer.links.pressMedia, href: "/about#press" },
        { label: t.footer.links.investorRelations, href: "/about#investors" },
      ],
    },
    {
      heading: t.footer.columns.resources,
      links: [
        { label: t.footer.links.academy, href: "/academy" },
        { label: t.footer.links.howItWorks, href: "/#how-it-works" },
        { label: t.footer.links.ephHub, href: "/for-manufacturers#eph" },
        { label: t.footer.links.sustainabilityReport, href: "/about#sustainability" },
        { label: t.footer.links.cadStandards, href: "/designers#cad-standards" },
        { label: t.footer.links.supplierCode, href: "/about#conduct" },
        { label: t.footer.links.inspire, href: "/inspire" },
        { label: t.footer.links.newsroom ?? "Newsroom", href: "/newsroom" },
        { label: t.footer.links.aiCredits ?? "AI Credits", href: "/ai/credits" },
      ],
    },
    {
      heading: t.footer.columns.contact,
      links: [
        { label: t.footer.links.contactUs, href: "/contact" },
        { label: "hello@caratzon.com", href: "mailto:hello@caratzon.com" },
        { label: "LinkedIn", href: "https://linkedin.com/company/caratzon", external: true },
      ],
    },
  ];

  const LEGAL_LINKS = [
    { label: t.footer.legal.terms, href: "/terms" },
    { label: t.footer.legal.privacy, href: "/privacy" },
    { label: t.footer.legal.cookies, href: "/cookies" },
    { label: t.footer.legal.supplierCode, href: "/supplier-code" },
  ];

  const OFFICES = [
    { city: "Hong Kong", note: t.footer.offices.hongkong },
    { city: "Guangdong", note: t.footer.offices.guangdong },
    { city: "London", note: t.footer.offices.london },
  ];

  return (
    <footer style={{ background: INK }} className="relative">
      {/* ── Brand block ─────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden px-6 lg:px-12 pt-20 pb-14 text-center">

        {/* Brilliant-cut diamond watermark */}
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
          aria-hidden="true"
        >
          <svg viewBox="0 0 400 400" width="640" height="640" style={{ opacity: 0.045 }}>
            <g style={{ transformOrigin: "200px 200px", animation: "footer-spin 120s linear infinite" }}>
              <circle cx="200" cy="200" r="168" fill="none" stroke={GOLD} strokeWidth="1.2" />
              <circle cx="200" cy="200" r="184" fill="none" stroke={GOLD} strokeWidth="0.5" strokeDasharray="3 8" />
              {(() => {
                const pts = Array.from({ length: 8 }, (_, i) => {
                  const a = ((i * 45 + 22.5) * Math.PI) / 180;
                  return [200 + 58 * Math.cos(a), 200 + 58 * Math.sin(a)];
                });
                return <polygon points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke={GOLD} strokeWidth="1" />;
              })()}
              {Array.from({ length: 8 }, (_, i) => {
                const ga = (i * 45 * Math.PI) / 180;
                const t1a = ((i * 45 + 22.5) * Math.PI) / 180;
                const t2a = (((i + 1) * 45 + 22.5) * Math.PI) / 180;
                const gx = 200 + 168 * Math.cos(ga); const gy = 200 + 168 * Math.sin(ga);
                const t1x = 200 + 58 * Math.cos(t1a); const t1y = 200 + 58 * Math.sin(t1a);
                const t2x = 200 + 58 * Math.cos(t2a); const t2y = 200 + 58 * Math.sin(t2a);
                return (
                  <g key={i} stroke={GOLD} strokeWidth="0.7" fill="none">
                    <line x1={gx} y1={gy} x2={t1x} y2={t1y} />
                    <line x1={gx} y1={gy} x2={t2x} y2={t2y} />
                  </g>
                );
              })}
              {Array.from({ length: 8 }, (_, i) => {
                const t1a = ((i * 45 + 22.5) * Math.PI) / 180;
                const s1a = (i * 45 * Math.PI) / 180;
                const s2a = (((i + 7) * 45) * Math.PI) / 180;
                const t1x = 200 + 58 * Math.cos(t1a); const t1y = 200 + 58 * Math.sin(t1a);
                const s1x = 200 + 114 * Math.cos(s1a); const s1y = 200 + 114 * Math.sin(s1a);
                const s2x = 200 + 114 * Math.cos(s2a); const s2y = 200 + 114 * Math.sin(s2a);
                return (
                  <g key={i} stroke={GOLD} strokeWidth="0.5" fill="none">
                    <line x1={t1x} y1={t1y} x2={s1x} y2={s1y} />
                    <line x1={t1x} y1={t1y} x2={s2x} y2={s2y} />
                  </g>
                );
              })}
            </g>
            <style>{`@keyframes footer-spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} } @media(prefers-reduced-motion:reduce){g{animation:none!important}}`}</style>
          </svg>
        </div>

        {/* Logo seal */}
        <div className="relative flex justify-center mb-6">
          <a href="/" className="inline-block transition-opacity hover:opacity-70" style={{ color: GOLD }}>
            <CaratzonLogo size={108} />
          </a>
        </div>

        <div className="relative flex items-center justify-center gap-5 mb-5">
          <div style={{ width: 48, height: "1px", background: RULE }} />
          <span style={{ fontFamily: "var(--font-sans)", fontSize: "0.56rem", letterSpacing: "0.32em", textTransform: "uppercase", color: TEXT_GHOST }}>
            Est. 2024
          </span>
          <div style={{ width: 48, height: "1px", background: RULE }} />
        </div>

        <a
          href="/"
          className="block hover:opacity-80 transition-opacity"
          style={{ fontFamily: "var(--font-display)", fontWeight: 300, letterSpacing: "0.48em", textTransform: "uppercase", fontSize: "clamp(2rem, 5vw, 3.6rem)", color: TEXT_PRIMARY, lineHeight: 1, paddingLeft: "0.48em" }}
        >
          Caratzon
        </a>

        <p className="mt-3" style={{ fontFamily: "var(--font-sans)", fontSize: "0.6rem", letterSpacing: "0.28em", textTransform: "uppercase", color: TEXT_GHOST }}>
          {t.footer.subline}
        </p>

        <p
          className="mt-6 mx-auto"
          style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic", fontSize: "clamp(0.9rem, 1.6vw, 1.15rem)", color: TEXT_DIM, lineHeight: 1.75, maxWidth: "36ch" }}
        >
          {t.footer.tagline.split("\n").map((line, i) => (
            <span key={i}>{line}{i === 0 && <br />}</span>
          ))}
        </p>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <a
            href="/sign-in"
            className="inline-block transition-colors"
            style={{ background: TEAL, color: "oklch(0.98 0 0)", fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase", fontFamily: "var(--font-sans)", padding: "0.85rem 2.25rem" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = TEAL_HOVER)}
            onMouseLeave={(e) => (e.currentTarget.style.background = TEAL)}
          >
            {t.footer.accessPlatform}
          </a>

          <a href="https://eph.eco/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition-opacity hover:opacity-80" style={{ border: `1px solid ${RULE}`, padding: "0.85rem 1.25rem" }}>
            <FacetMark />
            <span style={{ fontFamily: "var(--font-sans)", fontSize: "0.58rem", letterSpacing: "0.2em", textTransform: "uppercase", color: TEXT_GHOST }}>
              {t.footer.ephCertified}
            </span>
          </a>
        </div>
      </div>

      {/* ── Rule ── */}
      <div className="px-6 lg:px-12">
        <div style={{ height: "1px", background: RULE }} />
      </div>

      {/* ── Navigation columns ── */}
      <div className="px-6 lg:px-12 py-12 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-0">
        {COLUMNS.map((col, colIdx) => (
          <div key={col.heading} className="lg:border-l" style={{ borderColor: RULE }}>
            <div className="mb-5" style={{ paddingLeft: colIdx > 0 ? "1.5rem" : 0, paddingRight: "1.5rem" }}>
              <div style={{ height: "1px", background: RULE, marginBottom: "1.25rem" }} />
              <span style={{ fontFamily: "var(--font-sans)", fontSize: "0.58rem", letterSpacing: "0.28em", textTransform: "uppercase", color: TEXT_GHOST, display: "block", marginBottom: "1.25rem" }}>
                {col.heading}
              </span>
              <ul className="space-y-3">
                {col.links.map(({ label, href, external }: { label: string; href: string; external?: boolean }) => (
                  <li key={href}>
                    <a
                      href={href}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noopener noreferrer" : undefined}
                      className="group inline-flex items-center gap-1 transition-colors"
                      style={{ fontFamily: "var(--font-sans)", fontSize: "0.72rem", letterSpacing: "0.06em", color: TEXT_DIM }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = TEXT_PRIMARY)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_DIM)}
                    >
                      {label}
                      {external ? <ExternalIcon /> : null}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* ── Offices strip ── */}
      <div className="px-6 lg:px-12">
        <div style={{ height: "1px", background: RULE }} />
        <div className="py-6 flex flex-wrap gap-x-10 gap-y-3">
          {OFFICES.map(({ city, note }) => (
            <div key={city} className="flex items-center gap-3">
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: "0.78rem", color: TEXT_DIM, letterSpacing: "0.04em" }}>
                {city}
              </span>
              <span style={{ width: 24, height: "1px", background: RULE, display: "inline-block" }} />
              <span style={{ fontFamily: "var(--font-sans)", fontSize: "0.6rem", letterSpacing: "0.14em", textTransform: "uppercase", color: TEXT_GHOST }}>
                {note}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Legal bar ── */}
      <div className="px-6 lg:px-12">
        <div style={{ height: "1px", background: RULE }} />
        <div className="py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <span style={{ fontFamily: "var(--font-sans)", fontSize: "0.62rem", letterSpacing: "0.12em", color: TEXT_GHOST }}>
            © {year} {t.footer.copyright}
          </span>
          <div className="flex flex-wrap items-center gap-5">
            {LEGAL_LINKS.map(({ label, href }) => (
              <a
                key={href}
                href={href}
                className="transition-colors"
                style={{ fontFamily: "var(--font-sans)", fontSize: "0.6rem", letterSpacing: "0.14em", textTransform: "uppercase", color: TEXT_GHOST }}
                onMouseEnter={(e) => (e.currentTarget.style.color = TEXT_DIM)}
                onMouseLeave={(e) => (e.currentTarget.style.color = TEXT_GHOST)}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
